import React from 'react';
import { LedgerTransactionRow, MappedSingleLedgerData } from './use-single-ledger';
import { createCurrencyFormatter } from '../../utils/globalCurrency';

interface LedgerPrintableProps {
  title: string;
  data: MappedSingleLedgerData | null;
  startDate?: string;
  endDate?: string;
  currencySymbol?: string;
}

// Create currency formatter with provided symbol (defaults to empty string)
const createFormatCurrency = (currencySymbol: string = '') => {
  const formatter = createCurrencyFormatter(currencySymbol);
  return (amount: number) => {
    const isNegative = amount < 0;
    const formatted = formatter.format(Math.abs(amount), 2);
    return isNegative ? `-${formatted}` : formatted;
  };
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatQuantity = (quantity: number) => {
  return quantity.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const LedgerPrintable = React.forwardRef<HTMLDivElement, LedgerPrintableProps>(
  ({ title, data, startDate, endDate, currencySymbol = '' }, ref) => {
    // Create currency formatter with provided symbol
    const formatCurrency = createFormatCurrency(currencySymbol);
    const cellStyle: React.CSSProperties = {
      padding: '5px 6px',
      fontSize: '11px',
      borderBottom: '1px solid #e5e7eb',
    };

    const headerCellStyle: React.CSSProperties = {
      ...cellStyle,
      fontWeight: 600,
      color: '#6b7280',
      borderBottom: '2px solid #d1d5db',
      textTransform: 'uppercase',
      fontSize: '10px',
    };

        const renderRow = (item: LedgerTransactionRow) => (
      <tr key={item.id}>
        <td style={{ ...cellStyle, color: '#6b7280' }}>{formatDate(item.date)}</td>
        <td style={{ ...cellStyle }}>{item.action}</td>
        <td style={{ ...cellStyle }}>
          {item.object}
          {item.indirectObject ? ` → ${item.indirectObject}` : ''}
        </td>
        <td style={{ ...cellStyle, textAlign: 'center' }}>{formatQuantity(item.quantity)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          <span title={formatCurrency(item.amount)}>
            {formatCurrency(item.amount)}
          </span>
        </td>
      </tr>
    );

            const renderSide = (sideTitle: string, sideData: { items: LedgerTransactionRow[], total: number, openingBalance: number, closingBalance: number, hasBalanceCarriedDown: boolean } | undefined, oppositeSideData: { hasBalanceCarriedDown: boolean, closingBalance: number } | undefined) => {
      const items = sideData?.items ?? [];
      const total = sideData?.total ?? 0;
      const hasClosingBalance = sideData?.hasBalanceCarriedDown && sideData.closingBalance > 0;
      // Bal b/d appears on opposite side of Bal c/d
      const hasBalanceBroughtDown = oppositeSideData?.hasBalanceCarriedDown && oppositeSideData.closingBalance > 0;
      
      return (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#1f2937', marginBottom: '5px' }}>
            {sideTitle}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, textAlign: 'left', width: '22%' }}>Date</th>
                <th style={{ ...headerCellStyle, textAlign: 'left', width: '20%' }}>Action</th>
                <th style={{ ...headerCellStyle, textAlign: 'left', width: '28%' }}>Particular</th>
                <th style={{ ...headerCellStyle, textAlign: 'center', width: '10%' }}>Qty</th>
                <th style={{ ...headerCellStyle, textAlign: 'right', width: '20%' }}>Amount</th>
              </tr>
            </thead>
                        <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#6b7280' }}>
                    No transactions
                  </td>
                </tr>
              ) : (
                <>
                  {/* Transaction rows */}
                  {items.map(renderRow)}
                  
                                    {/* Balance Carried Down (B/C) row */}
                  {hasClosingBalance && (
                    <tr>
                      <td style={{ ...cellStyle, color: '#6b7280', borderBottom: 'none' }}></td>
                      <td style={{ ...cellStyle, borderBottom: 'none' }}></td>
                      <td style={{ ...cellStyle, fontStyle: 'italic', color: '#6b7280', borderBottom: 'none' }}>
                        Bal c/d
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center', borderBottom: 'none' }}></td>
                      <td style={{ ...cellStyle, textAlign: 'right', fontStyle: 'italic', color: '#6b7280', borderBottom: 'none' }}>
                        <span title={formatCurrency(sideData!.closingBalance)}>
                          {formatCurrency(sideData!.closingBalance)}
                        </span>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
                                                <tfoot>
              {/* Show total including balance carried down */}
              <tr style={{ borderTop: '2px solid #d1d5db' }}>
                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 600, fontSize: '10px' }}>
                  Total:
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600, fontSize: '10px' }}>
                  <span title={formatCurrency(total + (sideData?.closingBalance || 0))}>
                    {formatCurrency(total + (sideData?.closingBalance || 0))}
                  </span>
                </td>
              </tr>
            </tfoot>
                              </table>
                    {/* Balance Brought Down (B/B) row OR empty placeholder to maintain alignment */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '4px' }}>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, width: '22%' }}>&nbsp;</td>
                <td style={{ ...cellStyle, width: '20%' }}>&nbsp;</td>
                <td style={{ ...cellStyle, width: '28%', fontStyle: 'italic', color: '#6b7280', minHeight: '24px' }}>
                  {hasBalanceBroughtDown ? 'Bal b/d' : ' '}
                </td>
                <td style={{ ...cellStyle, width: '10%', textAlign: 'center' }}>&nbsp;</td>
                <td style={{ ...cellStyle, width: '20%', textAlign: 'right', fontStyle: 'italic', color: '#6b7280', minHeight: '24px' }}>
                  {hasBalanceBroughtDown ? (
                    <span title={formatCurrency(oppositeSideData!.closingBalance)}>
                      {formatCurrency(oppositeSideData!.closingBalance)}
                    </span>
                  ) : (
                    ' '
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div
        ref={ref}
        style={{
          width: '900px',
          background: '#ffffff',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1f2937',
          padding: '24px',
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>{title}</div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
            {(startDate || endDate) && (
              <span>
                {startDate && `From: ${startDate}`}
                {startDate && endDate && '  —  '}
                {endDate && `To: ${endDate}`}
              </span>
            )}
                        {data && (
              <span title={formatCurrency(data.balanceAmount)}>
                Balance: {formatCurrency(data.balanceAmount)} ({data.balanceType})
              </span>
            )}
          </div>
        </div>

                        <div style={{ display: 'flex', gap: '16px' }}>
          {renderSide('DR (Debit)', data?.debitSide, data?.creditSide)}
          <div style={{ width: '1px', background: '#d1d5db' }}></div>
          {renderSide('CR (Credit)', data?.creditSide, data?.debitSide)}
        </div>
      </div>
    );
  }
);

LedgerPrintable.displayName = 'LedgerPrintable';

export default LedgerPrintable;
